import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";

import { createAxiosInstance } from "api/axiosConfig";
import { SMSMESSAGE_APPOINTMENTS, SMSMESSAGE_PATIENTS } from "api/Service";
import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { useSmsTemplates } from "features/templates/hooks/useSmsTemplates";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { showMutationError } from "utils/showMutationError";

const formSchema = z.object({
  message: z
    .string()
    .min(1, { message: "Please provide content for message." }),
  smsTemplate: z.string().optional(),
  selectedIds: z.array(z.string()),
});

const SendSMSModal = ({ onClose, selectedItems, selectedRows, reportType }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  // Support legacy selectedRows (TanStack row selection object) from AppointmentsList
  const ids = selectedRows
    ? Object.keys(selectedRows).map((key) => key.split("-")[0])
    : (selectedItems ?? []).map((item) =>
        String(
          reportType === "appointments"
            ? item.appointmentId ?? item.patientId
            : item.patientId
        )
      );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message: "",
      smsTemplate: "",
      selectedIds: [...new Set(ids)],
    },
  });

  const { data: smsTemplates } = useSmsTemplates();

  const handleFormSubmit = async (formData) => {
    const reportTypeMap = {
      patients: "patientIds",
      appointments: "appointmentIds",
      immunisations: "patientIds",
      accounting: "patientIds",
    };

    const key = reportTypeMap[reportType ?? "patients"];

    if (!key) {
      toast.error("Invalid report type");
      return;
    }

    const parsedIds = formData.selectedIds.map((id) => parseInt(id, 10));

    const obj = {
      tenant: tenantVal,
      message: formData.message,
      [key]: parsedIds,
    };

    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}${
          reportType === "appointments"
            ? SMSMESSAGE_APPOINTMENTS
            : SMSMESSAGE_PATIENTS
        }`,
        obj
      );

      if (response.status === 200) {
        toast.success("SMS sent successfully.");
        onClose();
      } else {
        toast.error("Failed to send SMS.");
      }
    } catch (error) {
      showMutationError(error, "Failed to send SMS.");
    }
  };

  return (
    <Dialog open onClose={onClose} title="Send sms" width={1000}>
      <FormProvider {...form}>
        <form
          className="d-flex flex-column gap-3"
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("SendSMSModal"))}
        >
          <FormField name="smsTemplate" label="SMS Template" select required>
            <label className="requiredValidator mb-1">SMS Templates</label>
            <select
              className="form-control p15"
              name="message"
              {...form.register("smsTemplate", {
                onChange: (e) => {
                  form.setValue("message", e.target.value);
                  form.trigger("message");
                },
              })}
            >
              {smsTemplates?.map((option) => {
                if (!option.text)
                  return (
                    <option key={option.value} label={option.label} value={""}>
                      {option.label}
                    </option>
                  );
                return (
                  <option
                    key={option.value}
                    label={option.label}
                    value={option.text}
                  >
                    {option.label}
                  </option>
                );
              })}
            </select>
          </FormField>

          <FormField name="message" label="Message">
            <label className="requiredValidator mb-1">Message</label>
            <textarea
              className="form-control textareaform-control"
              rows="4"
              {...form.register("message")}
            />
            {form.formState.errors.message && (
              <span className="text-danger" style={{ fontSize: "12px" }}>
                {form.formState.errors.message.message}
              </span>
            )}
          </FormField>

          {form.formState.errors.selectedIds && (
            <div className="text-danger" style={{ fontSize: "12px" }}>
              {form.formState.errors.selectedIds.message}
            </div>
          )}

          <Button type="submit" className="ms-auto">
            Save
          </Button>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export { SendSMSModal };
